/**
 * Participant check-in QR for Event Calendar "My Check-In QR Codes" tab.
 * One QR per User._id; event filter enriches registration status per household member.
 */

export function formatParticipantQrDisplayName(userDoc) {
  if (!userDoc) return "";
  const name = [userDoc.first_name, userDoc.last_name].filter(Boolean).join(" ").trim();
  return name;
}

/**
 * @param {'self' | 'child'} participantType
 * @param {object|null} [userDoc] - used for self: Parent role → "Parent", else "Participant"
 */
export function formatParticipantQrRoleLabel(participantType, userDoc = null) {
  if (participantType === "child") return "Child";
  const roles = userDoc?.roles;
  const isParent =
    Array.isArray(roles) && roles.some((r) => String(r).trim() === "Parent");
  return isParent ? "Parent" : "Participant";
}

/**
 * Card title: "Child: Alex Irudayaraj" / "Parent: Backiaraj".
 * @param {{ displayName?: string, participantType?: string, roleLabel?: string }} entry
 */
export function formatParticipantQrTitle(entry) {
  const name = (entry?.displayName || "").trim() || "Participant";
  const role =
    (entry?.roleLabel || "").trim() ||
    formatParticipantQrRoleLabel(entry?.participantType === "child" ? "child" : "self");
  return `${role}: ${name}`;
}

/**
 * Side-list label: "Backiaraj (Parent)" / "Alex Irudayaraj (Child)".
 * @param {{ displayName?: string, participantType?: string, roleLabel?: string }} entry
 */
export function formatParticipantQrListLabel(entry) {
  const name = (entry?.displayName || "").trim() || "Participant";
  const role =
    (entry?.roleLabel || "").trim() ||
    formatParticipantQrRoleLabel(entry?.participantType === "child" ? "child" : "self");
  return `${name} (${role})`;
}

/** Short status for the household side list. */
export function formatParticipantQrListStatusLabel(entry) {
  if (!entry?.isRegistered || entry?.statusKey === "not-registered") return "Not Registered";
  return (entry?.statusLabel || "Registered").trim() || "Registered";
}

/**
 * Check-in badge for a registration attendance value.
 * @returns {{ key: string, label: string }}
 */
export function getParticipantQrCheckInStatus(attendanceRaw) {
  const key = (attendanceRaw != null ? String(attendanceRaw) : "").trim() || "Registered";
  if (key === "Attended" || key === "Walk-In") {
    return { key: "checked-in", label: "Already Checked-In" };
  }
  if (key === "Waitlisted") {
    return { key: "waitlisted", label: "Waitlisted" };
  }
  if (key === "No-Show") {
    return { key: "noshow", label: "No-Show" };
  }
  if (key === "Cancelled") {
    return { key: "unavailable", label: "Cancelled" };
  }
  return { key: "ready", label: "Registered" };
}

/**
 * @param {object|null} userInfo - signed-in user
 * @param {Array<object>} linkedChildrenUsers - User docs from getLinkedChildrenForParent
 * @returns {Array<{ userId: string, displayName: string, searchText: string, participantType: 'self' | 'child', roleLabel: string, titledDisplayName: string }>}
 */
export function buildEventCalendarParticipantQrEntries(userInfo, linkedChildrenUsers) {
  const selfId = userInfo?._id != null ? String(userInfo._id).trim() : "";
  if (!selfId) return [];

  const selfFullName = formatParticipantQrDisplayName(userInfo) || "Participant";
  const selfRole = formatParticipantQrRoleLabel("self", userInfo);
  const entries = [
    {
      userId: selfId,
      displayName: selfFullName,
      searchText: `${selfRole} ${selfFullName}`.toLowerCase(),
      participantType: "self",
      roleLabel: selfRole,
      titledDisplayName: formatParticipantQrTitle({
        displayName: selfFullName,
        participantType: "self",
        roleLabel: selfRole,
      }),
    },
  ];

  const roles = userInfo?.roles;
  const isParent =
    Array.isArray(roles) && roles.some((r) => String(r).trim() === "Parent");

  if (!isParent) return entries;

  const seen = new Set([selfId]);
  for (const child of linkedChildrenUsers || []) {
    const uid = child?._id != null ? String(child._id).trim() : "";
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    const name = formatParticipantQrDisplayName(child) || "Child";
    const roleLabel = formatParticipantQrRoleLabel("child", child);
    entries.push({
      userId: uid,
      displayName: name,
      searchText: `${roleLabel} ${name}`.toLowerCase(),
      participantType: "child",
      roleLabel,
      titledDisplayName: formatParticipantQrTitle({
        displayName: name,
        participantType: "child",
        roleLabel,
      }),
    });
  }

  return entries;
}

/**
 * Unique upcoming events that have at least one family registration.
 * Caller should pass upcoming/open rows only (past and completed excluded).
 * @param {Array<object>} registeredEventRows - getMyEvents rows
 * @returns {Array<{ eventId: string, eventName: string, start_datetime?: string }>}
 */
export function buildParticipantQrEventOptions(registeredEventRows) {
  const seen = new Set();
  const options = [];
  for (const row of registeredEventRows || []) {
    const eventId = row?._id != null ? String(row._id).trim() : "";
    if (!eventId || seen.has(eventId)) continue;
    seen.add(eventId);
    const eventName = (row.name != null ? String(row.name) : "").trim() || "Untitled event";
    options.push({
      eventId,
      eventName,
      start_datetime: row.start_datetime,
    });
  }
  return options;
}

/**
 * Enrich family QR entries with registration + check-in status for one event.
 * Empty `eventId` (All): every household member shows Registered status.
 * @param {Array<object>} entries
 * @param {Array<object>} registeredEventRows - getMyEvents rows (any status)
 * @param {string} eventId
 */
export function enrichParticipantQrEntriesForEvent(entries, registeredEventRows, eventId) {
  const eid = eventId != null ? String(eventId).trim() : "";
  if (!eid) {
    return (entries || []).map((entry) => ({
      ...entry,
      isRegistered: true,
      attendance: null,
      statusKey: "ready",
      statusLabel: "Registered",
    }));
  }

  const byUser = new Map();
  for (const row of registeredEventRows || []) {
    const rowEventId = row?._id != null ? String(row._id).trim() : "";
    if (!rowEventId || rowEventId !== eid) continue;
    const uid =
      row.participant_user_id != null
        ? String(row.participant_user_id).trim()
        : row.user_id != null
          ? String(row.user_id).trim()
          : "";
    if (!uid) continue;
    byUser.set(uid, row);
  }

  return (entries || []).map((entry) => {
    const row = byUser.get(entry.userId);
    if (!row) {
      return {
        ...entry,
        isRegistered: false,
        attendance: null,
        statusKey: "not-registered",
        statusLabel: "Not Registered",
      };
    }
    const attendance = (row.attendance && String(row.attendance).trim()) || "Registered";
    const status = getParticipantQrCheckInStatus(attendance);
    return {
      ...entry,
      isRegistered: true,
      attendance,
      statusKey: status.key,
      statusLabel: status.label,
    };
  });
}

export function participantQrEntryMatchesSearch(entry, searchLower) {
  if (!searchLower) return true;
  const text = (
    entry?.searchText ||
    entry?.titledDisplayName ||
    entry?.displayName ||
    ""
  ).toLowerCase();
  return text.includes(searchLower);
}

/**
 * Registered members first, then not registered; by display name within each group.
 * @param {Array<object>} entries
 */
export function sortParticipantQrEntriesByRegistered(entries) {
  return [...(entries || [])].sort((a, b) => {
    const ar = a?.isRegistered ? 0 : 1;
    const br = b?.isRegistered ? 0 : 1;
    if (ar !== br) return ar - br;
    const an = (a?.displayName || a?.titledDisplayName || "").toLowerCase();
    const bn = (b?.displayName || b?.titledDisplayName || "").toLowerCase();
    return an.localeCompare(bn);
  });
}
